import { z } from "zod";
import { creditCertaintySchema, runStateSchema } from "./states";

/**
 * One row in `GET /api/v1/projects/:id/runs`, newest first. The credit figure is the confirmed
 * spend, or null when the run has not settled. A run is never reported as succeeded before its
 * state is confirmed: the state here is the reconciled one.
 */
export const runListEntrySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  workItemId: z.uuid().nullable(),
  workItemTitle: z.string().nullable(),
  /** What the run was asked to do. A run without a work item has only this to identify it. */
  intent: z.string(),
  flowVersionId: z.uuid().nullable(),
  flowVersion: z.string().nullable(),
  state: runStateSchema,
  progress: z.number().int().min(0).max(100),
  startedBy: z.string().min(1),
  creditSpend: z.number().int().nullable(),
  creditCertainty: creditCertaintySchema,
  startedAt: z.iso.datetime(),
});
export type RunListEntry = z.infer<typeof runListEntrySchema>;

/** The runs list filters, as the Runs board labels them. */
export const runListFilters = ["all", "waiting", "running", "unknown", "failed"] as const;
export const runListFilterSchema = z.enum(runListFilters);
export type RunListFilter = z.infer<typeof runListFilterSchema>;

export const runListQuerySchema = z.object({
  state: runListFilterSchema.optional().default("all"),
  page: z.coerce.number().int().min(1).optional().default(1),
  pageSize: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type RunListQuery = z.infer<typeof runListQuerySchema>;

/** A paginated runs answer. Totals count the filtered set, never another project's runs. */
export const runListResponseSchema = z.object({
  runs: z.array(runListEntrySchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
});
export type RunListResponse = z.infer<typeof runListResponseSchema>;

/** Runs this week split by outcome, for the overview cards. */
export const runsByOutcomeSchema = z.object({
  succeeded: z.number().int().min(0),
  failed: z.number().int().min(0),
  cancelled: z.number().int().min(0),
  unknown: z.number().int().min(0),
});
export type RunsByOutcome = z.infer<typeof runsByOutcomeSchema>;

/** One item needing the caller, as the overview needs-you list shows it. */
export const needsYouItemSchema = z.object({
  kind: z.enum(["approval", "run", "invitation"]),
  id: z.uuid(),
  title: z.string().min(1),
  href: z.string().min(1),
});
export type NeedsYouItem = z.infer<typeof needsYouItemSchema>;

/**
 * `GET /api/v1/projects/:id/overview`. Read-only aggregates over one project: the figures match
 * the underlying rows. Unknown spend stays visible as unknown, never zero.
 */
export const overviewSchema = z.object({
  projectId: z.uuid(),
  runsInProgress: z.number().int().min(0),
  runsThisWeek: runsByOutcomeSchema,
  creditsUsedThisMonth: z.number().int().min(0),
  creditsUsedCertainty: creditCertaintySchema,
  evidenceCompletion: z.number().int().min(0).max(100).nullable(),
  needsYou: z.array(needsYouItemSchema),
  creditBalance: z.number().int().nullable(),
});
export type Overview = z.infer<typeof overviewSchema>;
